import { useState, useEffect } from 'react'
import { PatientFile } from '../types/patientFile'
import { patientFileService } from '../services/patientFileService'
import './PatientFileViewer.css'

interface PatientFileViewerProps {
  file: PatientFile
  onClose: () => void
}

type ViewableType = 'image' | 'pdf' | 'text' | 'unsupported'

function getViewableType(fileType: string): ViewableType {
  if (fileType.startsWith('image/')) {
    return 'image'
  }
  if (fileType === 'application/pdf') {
    return 'pdf'
  }
  if (
    fileType.startsWith('text/') ||
    fileType === 'application/json' ||
    fileType === 'application/xml'
  ) {
    return 'text'
  }
  return 'unsupported'
}

export function PatientFileViewer({ file, onClose }: PatientFileViewerProps) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [content, setContent] = useState<string | null>(null)
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const viewableType = getViewableType(file.fileType)

  useEffect(() => {
    if (viewableType === 'unsupported') {
      setLoading(false)
      return
    }

    const loadFile = async () => {
      try {
        setLoading(true)
        setError(null)

        if (viewableType === 'image' || viewableType === 'pdf') {
          // For images and PDFs, use the file URL directly or download and create blob URL
          if (file.fileUrl && (file.fileUrl.startsWith('http') || file.fileUrl.startsWith('blob:'))) {
            console.log('[PatientFileViewer] Using existing fileUrl:', file.fileUrl)
            setBlobUrl(file.fileUrl)
          } else {
            console.log('[PatientFileViewer] Downloading file to create blob URL...')
            const blob = await patientFileService.download(file.id)
            if (blob.size === 0) {
              throw new Error('Downloaded file is empty')
            }
            console.log('[PatientFileViewer] Creating blob URL from blob:', {
              blobSize: blob.size,
              blobType: blob.type,
            })
            const url = window.URL.createObjectURL(blob)
            console.log('[PatientFileViewer] Blob URL created:', url)
            setBlobUrl(url)
            console.log('[PatientFileViewer] Blob URL set in state')
          }
        } else if (viewableType === 'text') {
          // For text files, download and read as text
          const blob = await patientFileService.download(file.id)
          if (blob.size === 0) {
            throw new Error('Downloaded file is empty')
          }
          const text = await blob.text()
          setContent(text)
        }

        setLoading(false)
      } catch (err: any) {
        console.error('[PatientFileViewer] Error loading file:', err)
        const errorMessage = err?.response?.data?.message || err?.message || 'Failed to load file'
        setError(errorMessage)
        setLoading(false)
      }
    }

    loadFile()

    // Cleanup blob URL on unmount or when file changes
    return () => {
      // Use a ref or check current blobUrl state
      // We'll use a closure to capture the blobUrl at the time cleanup runs
    }
  }, [file.id, file.fileType, viewableType])
  
  // Separate effect for cleanup to avoid dependency issues
  useEffect(() => {
    return () => {
      if (blobUrl && blobUrl.startsWith('blob:')) {
        console.log('[PatientFileViewer] Cleaning up blob URL:', blobUrl)
        window.URL.revokeObjectURL(blobUrl)
      }
    }
  }, [blobUrl])

  const handleDownload = async () => {
    try {
      const blob = await patientFileService.download(file.id)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = file.fileName
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (err: any) {
      alert(err?.message || 'Failed to download file')
    }
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  if (viewableType === 'unsupported') {
    return (
      <div className="patient-file-viewer-overlay" onClick={handleBackdropClick}>
        <div className="patient-file-viewer-container">
          <div className="patient-file-viewer-header">
            <h3>{file.displayName || file.fileName}</h3>
            <button className="patient-file-viewer-close" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>
          <div className="patient-file-viewer-content">
            <div className="patient-file-viewer-unsupported">
              <p>This file type cannot be viewed in the browser.</p>
              <button className="btn-primary" onClick={handleDownload}>
                Download File
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="patient-file-viewer-overlay" onClick={handleBackdropClick}>
      <div className="patient-file-viewer-container">
        <div className="patient-file-viewer-header">
          <h3>{file.displayName || file.fileName}</h3>
          <div className="patient-file-viewer-actions">
            <button className="btn-secondary" onClick={handleDownload}>
              Download
            </button>
            <button className="patient-file-viewer-close" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>
        </div>
        <div className="patient-file-viewer-content">
          {loading && (
            <div className="patient-file-viewer-loading">
              <p>Loading file...</p>
            </div>
          )}
          {error && (
            <div className="patient-file-viewer-error">
              <p>Error: {error}</p>
              <button className="btn-primary" onClick={handleDownload}>
                Download File Instead
              </button>
            </div>
          )}
          {!loading && !error && viewableType === 'image' && blobUrl && (
            <div className="patient-file-viewer-image">
              <img 
                src={blobUrl} 
                alt={file.displayName || file.fileName}
                onLoad={() => console.log('[PatientFileViewer] Image loaded successfully')}
                onError={(e) => {
                  console.error('[PatientFileViewer] Image failed to load:', e)
                  console.error('[PatientFileViewer] Blob URL:', blobUrl)
                  console.error('[PatientFileViewer] File type:', file.fileType)
                }}
              />
            </div>
          )}
          {!loading && !error && viewableType === 'pdf' && blobUrl && (
            <div className="patient-file-viewer-pdf">
              <iframe src={blobUrl} title={file.displayName || file.fileName} />
            </div>
          )}
          {!loading && !error && viewableType === 'text' && content !== null && (
            <div className="patient-file-viewer-text">
              <pre>{content}</pre>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

